import { makeStyles } from '@material-ui/core'
import { MessageOutlined as MessageIcon } from '@material-ui/icons'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'

import { useActionRunner } from 'src/modules/notification'
import { Button, FormikDialog, Order, Typography } from 'src/modules/ui'

import EditOrder from './EditOrder'
import MessageBuyerModal from '../../messages/components/MessageBuyerModal'
import { useOrders } from '../hooks'

const useStyles = makeStyles(theme => ({
  orderAction: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
}))

const EditPanelItems = ({ id, onClose, noteOnly }) => {
  const classes = useStyles()
  const { submitForm, values, validateForm } = useFormikContext()
  const { updateOrder } = useOrders()

  const update = async () => {
    await submitForm()
    const errors = await validateForm()
    if (Object.keys(errors).length !== 0) {
      return
    }
    const result = await doUpdateOrder({ id, params: values })
    try {
      unwrapResult(result)
      onClose()
    } catch (error) {}
  }

  const [doUpdateOrder] = useActionRunner(updateOrder, {
    successNotification: 'Order updated!',
  })

  return (
    <>
      <Button
        className={classes.orderAction}
        color="lightGreen"
        fullWidth
        onClick={update}
      >
        {noteOnly ? 'Add Note' : 'Update Order'}
      </Button>
      <Button
        className={classes.orderAction}
        color="lightGreen"
        fullWidth
        onClick={onClose}
      >
        Cancel
      </Button>
    </>
  )
}

export const SingleOrder = ({ buyer, environmentId, id, status, ...order }) => {
  const classes = useStyles()
  const { markOrderDispatched, cancelOrder } = useOrders()
  const [editingOrder, setEditingOrder] = useState(false)
  const [addingNote, setAddingNote] = useState(false)

  const editOrder = () => {
    setEditingOrder(true)
  }

  const addNote = () => {
    setAddingNote(true)
  }

  const cancelEdit = () => {
    setEditingOrder(false)
  }

  const cancelAddNote = () => {
    setAddingNote(false)
  }

  if (editingOrder) {
    return (
      <EditOrder
        {...order}
        id={id}
        environmentId={environmentId}
        organisation={buyer}
        status={status}
        noteOnly={false}
        sidePanelItems={
          <EditPanelItems id={id} onClose={cancelEdit} noteOnly={false} />
        }
      />
    )
  }

  if (addingNote) {
    return (
      <EditOrder
        {...order}
        id={id}
        environmentId={environmentId}
        organisation={buyer}
        status={status}
        noteOnly={true}
        sidePanelItems={
          <EditPanelItems id={id} onClose={cancelAddNote} noteOnly={true} />
        }
      />
    )
  }

  return (
    <Order
      {...order}
      id={id}
      environmentId={environmentId}
      organisation={buyer}
      status={status}
      showDisputed={false}
      sidePanelItems={
        <>
          {(status === 'submitted') | (status === 'updated_by_supplier') ? (
            <>
              <FormikDialog
                onSubmit={() => markOrderDispatched(id)}
                submitText="Mark As Dispatched"
                successNotification={{
                  image: 'thyme',
                  message:
                    'Order marked as dispatched.\n\nLooking forward to the ride! Better get going... we need to arrive in thyme!',
                }}
                trigger={({ onClick }) => (
                  <Button
                    className={classes.orderAction}
                    color="lightGreen"
                    fullWidth
                    onClick={onClick}
                  >
                    Mark As Dispatched
                  </Button>
                )}
              >
                <Typography>Mark As Dispatched?</Typography>
              </FormikDialog>
              <FormikDialog
                onSubmit={() => cancelOrder(id)}
                submitText="Cancel Order"
                successNotification="Order cancelled"
                trigger={({ onClick }) => (
                  <Button
                    className={classes.orderAction}
                    color="lightGreen"
                    fullWidth
                    onClick={onClick}
                  >
                    Cancel Order
                  </Button>
                )}
              >
                <Typography>Cancel This Order?</Typography>
              </FormikDialog>
            </>
          ) : null}

          <>
            {(status === 'submitted') | (status === 'updated_by_supplier') ? (
              <Button
                className={classes.orderAction}
                color="lightGreen"
                fullWidth
                onClick={editOrder}
              >
                Edit Order
              </Button>
            ) : null}
            {status === 'issue_raised' ? (
              <Button
                className={classes.orderAction}
                color="lightGreen"
                fullWidth
                onClick={addNote}
              >
                Add Note
              </Button>
            ) : null}
            <MessageBuyerModal
              environmentId={environmentId}
              buyer={buyer}
              subject={`Re: Order ${id}`}
              trigger={props => (
                <Button
                  color="white"
                  fullWidth
                  startIcon={<MessageIcon />}
                  {...props}
                >
                  Contact Buyer
                </Button>
              )}
            />
          </>
        </>
      }
    />
  )
}

SingleOrder.propsTypes = {
  id: PropTypes.number,
  environmentId: PropTypes.number,
  buyer: PropTypes.object,
  status: PropTypes.string,
}

const SingleOrderContainer = ({ match }) => {
  const { fetchSingleOrder, order } = useOrders()
  const [doFetchSingleOrder, isFetching] = useActionRunner(fetchSingleOrder, {
    initialFetchingState: true,
  })
  const { id } = match.params

  useEffect(() => {
    doFetchSingleOrder(id)
  }, [id])

  if (isFetching) {
    return null
  }

  return <SingleOrder {...order} />
}

export default SingleOrderContainer
